import { useTranslation } from 'react-i18next';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import { OpBanner } from 'new-components/DLS/OpBanner/OpBanner';
import { OpFlex } from 'new-components/DLS/OpFlex/OpFlex';
import { OpText } from 'new-components/DLS/OpText/OpText';
import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';
import { DATE_FORMAT } from 'constants/Dates';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { getOpParams } from 'utils/getOpParams';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { useOpMutation } from 'utils/customHooks/useOpMutation';

import { useTrialBanner } from './hooks/useTrialBanner';

/**
 * Day count threshold at which the alert banner should be enhanced (e.g., changed from grey to red).
 * When daysLeft < this value, the banner should display with danger styling.
 */
const ENHANCED_ALERT_BANNER_THRESHOLD_DAYS = 30;

export const TrialWarningBanner = () => {
  const { t } = useTranslation();

  // This will be used to close the modal after submit succeeds
  const modalRef = useRef<ReturnType<typeof confirmModal>>(undefined);
  const confirmModal = useConfirmModal();

  const navigate = useNavigate();
  const { orgId } = getOpParams();

  const { daysLeft, billingStartsAt, isLoading, showBanner } = useTrialBanner();

  const cancelMailroom = useOpMutation({
    apiEndpointName: 'cancelMailroom',
    queryKeysToInvalidate: [
      ['describeMailroom'],
      ['listMailrooms'],
      ['describeParcel'],
      ['listParcels'],
      ['describeOrg'],
      ['listOrgSoftwareLicenses'],
    ],
    onSuccessCallback: () => {
      modalRef.current?.destroy();
      navigate(`/o/${orgId}/trial-end`);
    },
  });

  const handleSubmit = useCallback(async () => {
    await cancelMailroom.mutateAsync({
      apiEndpointRequirements: [orgId],
    });
  }, [cancelMailroom, orgId]);

  const [form] = OpForm.useForm();

  const handleCancelTrial = () => {
    modalRef.current = confirmModal({
      title: t('Cancel Mailroom trial?'),
      okText: t('Cancel trial'),
      okButtonProps: {
        htmlType: 'submit',
        variant: 'solid',
        color: 'danger',
        // give button different aria name as other button has same label
        'aria-label': 'Confirm cancel trial',
      },
      // Needs _keepThis so the modal stays open to show form errors
      // Modal is closed in onSuccessCallback
      onOk: (_keepThis) => form.submit(),
      content: (
        <OpForm
          form={form}
          hasError={false}
          onSubmit={handleSubmit}
          defaultButtons={false}
        >
          <OpSpace orientation="vertical">
            <OpText>
              {t(
                'Your trial will end immediately and all mailrooms and package data will be permanently deleted.',
              )}
            </OpText>
            <OpForm.Input
              name="cancel-input"
              rules={[
                {
                  required: true,
                  pattern: /^cancel$/i,
                  message: t('Confirmation is required'),
                },
              ]}
              // Not translating the confirmation word as the user should always type "cancel"
              label={t('To confirm, type "{{message}}"', { message: 'cancel' })}
            />
          </OpSpace>
        </OpForm>
      ),
    });
  };

  // Don't show banner if trials are not enabled or active
  if (isLoading || !showBanner) return null;

  const title = (
    <OpFlex justify="space-between" testId="trial-warning-banner">
      <OpText>
        {t(
          '{{ count }} days left in your Mailroom trial. Billing begins on {{ billingStartsAt }}.',
          {
            count: daysLeft, // Using count for pluralization
            billingStartsAt: billingStartsAt.format(DATE_FORMAT),
          },
        )}
      </OpText>
      <OpTextButton onClick={handleCancelTrial}>
        {t('Cancel trial')}
      </OpTextButton>
    </OpFlex>
  );

  return (
    <OpBanner
      type={daysLeft < ENHANCED_ALERT_BANNER_THRESHOLD_DAYS ? 'error' : 'info'}
      title={title}
    />
  );
};
